import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Loader2, Save, Check, Undo2, RefreshCw } from 'lucide-react';

/**
 * StatusLetterEditor · iter300b
 * ─────────────────────────────
 * Inline editor for the 3 status letters that currently have PDF
 * designs (Appraisal Authorisation, Cover Letter, Testimonial). Loads
 * cells from `GET  /app-settings/status-letters/{status_key}` and
 * saves them via debounced (900ms) PUT to the same URL. Reuses the
 * same UX conventions as the Quotation Letter editor:
 *   • Auto-save with per-cell dirty/saved indicators
 *   • Per-cell undo (Ctrl+Z + ↺ button — ring buffer of 25)
 *   • Token palette below the client-header (all Quotation tokens work)
 *   • "⚠ using default" badge on empty cells
 *
 * Cell configs are declarative (per status_key) so adding more
 * statuses later is a one-line change — no JSX duplication.
 */

// Token palette groups — imported behaviour, copy-pasted from
// QuotationLetterEditor since the palette component is inlined there.
// All these tokens resolve via `_build_quotation_tokens` on the backend.
const TOKEN_GROUPS = [
  { label: 'Client',   tokens: ['Client_name', 'client_first_name', 'ethnic_greeting', 'Client_email', 'Client_phone'] },
  { label: 'Property', tokens: ['Property_Address', 'Property_Suburb', 'Property_State', 'Property_Postcode'] },
  { label: 'Roof',     tokens: ['roof_type', 'roof_pitch', 'roof_area', 'storeys', 'roof_m2_display', 'roof_colour', 'roof_brand', 'roof_age', 'roof_material', 'roof_condition', 'roof_state', 'organic_growth', 'roof_description', 'ridge_length', 'barge_length', 'valley_length', 'perimeter_length'] },
  { label: 'Job',      tokens: ['quote_type', 'quote_total', 'deposit_amount', 'quote_ref', 'commencement_window', 'approx_start_time', 'accepted_quote_summary', 'declined_quote_summary', 'invoice_no'] },
  { label: 'Company',  tokens: ['Company_Bank_Name', 'Company_Account_Number', 'Company_Account_Holder', 'after_hours_phone'] },
  { label: 'Terms',    tokens: ['quote_valid_days', 'quote_valid_until', 'quote_date', 'representative_name'] },
];

// Cell definitions per status letter — order = PDF render order.
// `kind: 'lines'` renders as a multi-line list where each line is one
// item (used for `interest_items` on the Auth Letter).
const STATUS_CELL_CONFIG = {
  appraisal_booked: {
    label: 'Appraisal Authorisation Letter',
    cells: [
      { key: 'title',              hint: 'Big centred title at the top of the PDF',                   rows: 1 },
      { key: 'client_greeting',    hint: 'Personalised greeting — first line under the Client IDs block. Mirrors Invoice Letter Cell 2 title. Tokens: {Client_name}, {ethnic_greeting}, {ethnic_thankyou}', rows: 2 },
      { key: 'purpose_body',       hint: 'Opening paragraph — explains this is not an obligation',    rows: 3 },
      { key: 'details_prefix',     hint: 'One-line intro to the client-details verification block',   rows: 2 },
      { key: 'client_details_items', hint: 'One row per line. Format "Label: {token}". Renders as a compact 2-column table with a blank underline for handwritten corrections beside each row. Tokens: {Client_name}, {Client_Address}, {Client_Postal_Address}, {Client_phone}, {Client_email}, {Property_Address}', rows: 5, kind: 'lines' },
      { key: 'authorisation_body', hint: 'The auth clause the client is agreeing to (bold tokens OK)', rows: 3 },
      { key: 'quality_statement',  hint: 'Company-quality blurb shown below the signature line',      rows: 3 },
      { key: 'interests_prompt',   hint: 'Sub-heading above the ☐ checkboxes',                        rows: 1 },
      { key: 'interest_items',     hint: 'One checkbox per line. Rendered as ☐ items in the PDF.',   rows: 5, kind: 'lines' },
      { key: 'disclaimer',         hint: 'Small-print at the very bottom (credit / liability)',       rows: 3 },
    ],
  },
  quote_sent: {
    label: 'Cover Letter (Quote Sent)',
    cells: [
      { key: 'client_greeting', hint: 'Personalised greeting — first line under the Client IDs block. Replaces the old "Dear …" line. Tokens: {Client_name}, {ethnic_greeting}, {ethnic_thankyou}', rows: 2 },
      { key: 're_prefix',    hint: 'Prefix for the RE: line — usually "RE:"', rows: 1 },
      { key: 'intro_body',   hint: 'Intro paragraph — references the enclosed quotation',        rows: 3 },
      { key: 'history_body', hint: 'Company-history paragraph (2 industries / decades)',         rows: 3 },
      { key: 'ethos_body',   hint: 'Workmanship-ethos paragraph (old + new practices)',          rows: 3 },
      { key: 'closing_body', hint: 'Closing paragraph — invites questions',                      rows: 3 },
    ],
  },
  paid: {
    label: 'Testimonial Request Letter (Paid)',
    cells: [
      { key: 'client_greeting',      hint: 'Personalised greeting — first line under the Client IDs block. Replaces the old "Dear …" line. Tokens: {Client_name}, {ethnic_greeting}, {ethnic_thankyou}', rows: 2 },
      { key: 'intro_body',           hint: '"I have a favour to ask of you."',                       rows: 1 },
      { key: 'body_1',               hint: 'Testimonial ask + question',                             rows: 3 },
      { key: 'body_2',               hint: 'Instruction re: attached questionnaire',                 rows: 2 },
      { key: 'photo_permission',     hint: 'Photo-permission ask (before/after)',                    rows: 2 },
      { key: 'welcome_feedback',     hint: 'Open-to-criticism line',                                 rows: 2 },
      { key: 'closing_thanks',       hint: '"Many thanks."',                                         rows: 1 },
      { key: 'signoff',              hint: '"Sincerely,"',                                           rows: 1 },
      { key: 'signer_name',          hint: 'Name printed under sign-off',                            rows: 1 },
      { key: 'signer_title',         hint: 'Title printed under name',                               rows: 1 },
      { key: 'permission_statement', hint: 'ALL CAPS permission block above signature line',        rows: 3 },
    ],
  },
};

const UNDO_LIMIT = 25;

export default function StatusLetterEditor({ api, statusKey, onSaved }) {
  const staticConfig = STATUS_CELL_CONFIG[statusKey];
  // Letters without a hand-written config get one cell per stored key.
  const [genericCells, setGenericCells] = useState([]);
  const config = staticConfig || { label: statusKey.replace(/_/g, ' '), cells: genericCells };
  const [drafts, setDrafts] = useState({});
  const [initial, setInitial] = useState({}); // for dirty comparison
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState({});
  const [savedAt, setSavedAt] = useState({});
  const [undoStacks, setUndoStacks] = useState({});
  const debounceRefs = useRef({});
  const lastFocusedRef = useRef(null);

  // ─── Load ────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get(`/app-settings/status-letters/${statusKey}`);
      // interest_items may be an array — flatten to newline string for
      // the textarea to edit as one item per line.
      const flat = { ...data };
      if (Array.isArray(flat.interest_items)) flat.interest_items = flat.interest_items.join('\n');
      if (!staticConfig) {
        setGenericCells(Object.keys(flat).filter(k => k !== 'is_draft').map(k => ({
          key: k,
          hint: k.replace(/_/g, ' '),
          rows: /(_body|_items|_summary)$/.test(k) ? 4 : (/(title|prefix|signoff|signer_name|signer_title)$/.test(k) ? 1 : 2),
        })));
      }
      setDrafts(flat);
      setInitial(flat);
      setUndoStacks({});
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to load status letter content');
    } finally {
      setLoading(false);
    }
  }, [api, statusKey, staticConfig]);

  useEffect(() => { load(); }, [load]);

  // ─── Save one cell (debounced 900 ms) ───────────────────────────────
  const saveCell = useCallback(async (key, value) => {
    try {
      await api.put(`/app-settings/status-letters/${statusKey}`, { [key]: value });
      setSavedAt(s => ({ ...s, [key]: Date.now() }));
      setDirty(d => ({ ...d, [key]: false }));
      setInitial(i => ({ ...i, [key]: value }));
      if (onSaved) onSaved();
    } catch (e) {
      setError(`Save failed for ${key}: ${e?.response?.data?.detail || e.message}`);
    }
  }, [api, statusKey, onSaved]);

  const setField = (key, value) => {
    // Push previous value onto undo stack IF it's actually changing.
    setDrafts(d => {
      const prev = d[key] ?? '';
      if (prev !== value) {
        setUndoStacks(s => {
          const stack = s[key] || [];
          return { ...s, [key]: [...stack.slice(-UNDO_LIMIT + 1), prev] };
        });
      }
      return { ...d, [key]: value };
    });
    setDirty(x => ({ ...x, [key]: true }));
    if (debounceRefs.current[key]) clearTimeout(debounceRefs.current[key]);
    debounceRefs.current[key] = setTimeout(() => saveCell(key, value), 900);
  };

  const undoField = (key) => {
    const stack = undoStacks[key] || [];
    if (stack.length === 0) return;
    const prev = stack[stack.length - 1];
    if (debounceRefs.current[key]) clearTimeout(debounceRefs.current[key]);
    setUndoStacks(s => ({ ...s, [key]: stack.slice(0, -1) }));
    setDrafts(d => ({ ...d, [key]: prev }));
    saveCell(key, prev);
  };

  // ─── Token palette insertion ────────────────────────────────────────
  const onFieldFocus = useCallback((e) => {
    const el = e.currentTarget;
    const key = el.dataset.fieldKey;
    if (!key) return;
    lastFocusedRef.current = { el, key };
  }, []);

  const insertToken = useCallback((tokenKey) => {
    const focus = lastFocusedRef.current;
    if (!focus) {
      setError('Click into a text field first, then a token chip to insert it.');
      return;
    }
    const { el, key } = focus;
    const start = el.selectionStart ?? (el.value || '').length;
    const end = el.selectionEnd ?? start;
    const current = drafts[key] ?? '';
    const insertText = `{${tokenKey}}`;
    setField(key, current.slice(0, start) + insertText + current.slice(end));
    requestAnimationFrame(() => {
      try {
        el.focus();
        const caret = start + insertText.length;
        el.setSelectionRange(caret, caret);
      } catch { /* unmounted */ }
    });
  }, [drafts]);

  // Empty-cell count for the header badge.
  const emptyCount = useMemo(() => {
    if (!config) return 0;
    return config.cells.filter(c => !(drafts[c.key] || '').toString().trim()).length;
  }, [drafts, config]);

  if (!config) {
    return (
      <div className="col-span-12 lg:col-span-7 bg-white border border-slate-200 rounded-md p-5" data-testid="status-editor-unsupported">
        <p className="text-sm italic text-slate-500">Status &ldquo;{statusKey}&rdquo; has no editor yet — the backend still returns HTTP 501 for the PDF. Provide a sample from Access DB to unlock this tab.</p>
      </div>
    );
  }

  return (
    <div className="col-span-12 lg:col-span-7 bg-[#FAFAF7] border border-[#E2E2DF] rounded-md flex flex-col max-h-[calc(100vh-200px)]" data-testid={`status-editor-${statusKey}`}>
      {/* Header */}
      <div className="px-4 py-2 bg-white border-b border-slate-200 flex items-center gap-3">
        <h3 className="text-sm font-bold text-slate-900">{config.label} — Editor</h3>
        <span data-testid="pdf-ready-badge" className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 border border-emerald-300">✓ PDF ready</span>
        <span className="text-[10px] text-slate-500 italic">Auto-saves after 900 ms · Ctrl+Z / ↺ per cell</span>
        {emptyCount > 0 && (
          <span className="text-[10px] font-bold uppercase tracking-wider text-amber-800 bg-amber-100 border border-amber-300 rounded px-1.5">
            {emptyCount} using default
          </span>
        )}
        <button
          type="button" onClick={load}
          className="ml-auto flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold text-slate-700 border border-slate-300 rounded hover:bg-slate-100"
          title="Reload from database (discards unsaved drafts)"
          data-testid={`status-editor-reload-${statusKey}`}
        >
          <RefreshCw size={10} /> Reload
        </button>
      </div>

      {error && (
        <div className="mx-4 mt-3 px-3 py-2 bg-red-50 border border-red-200 text-red-700 text-xs rounded flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-2">✕</button>
        </div>
      )}

      {loading ? (
        <div className="p-8 text-center text-sm text-slate-500 flex items-center justify-center gap-2">
          <Loader2 className="animate-spin" size={14} /> Loading…
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* Token palette */}
          <TokenPalette onInsert={insertToken} />
          {/* Cells */}
          {config.cells.map(cell => {
            const value = drafts[cell.key] ?? '';
            const missing = !value.toString().trim();
            const isDirty = !!dirty[cell.key];
            const showSaved = savedAt[cell.key] && Date.now() - savedAt[cell.key] < 3500;
            const canUndo = (undoStacks[cell.key] || []).length > 0;
            return (
              <div key={cell.key} className={`border rounded-md overflow-hidden ${missing ? 'border-amber-300 bg-amber-50/30' : 'border-emerald-300 bg-white'}`}>
                <div className={`px-3 py-2 border-b flex items-center gap-2 flex-wrap ${missing ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50 border-emerald-200'}`}>
                  <span className="text-[10px] font-mono font-bold text-slate-600">{cell.key}</span>
                  {missing && (
                    <span className="text-[10px] font-bold uppercase tracking-wider text-amber-800 bg-amber-100 border border-amber-300 rounded px-1.5">
                      ⚠ using default (empty)
                    </span>
                  )}
                  <span className="text-[10px] italic text-slate-500 flex-1 min-w-0 truncate">{cell.hint}</span>
                  {canUndo && (
                    <button
                      type="button" onClick={() => undoField(cell.key)}
                      className="text-slate-500 hover:text-slate-900"
                      title="Undo (also: Ctrl+Z inside the textarea)"
                      data-testid={`status-${statusKey}-${cell.key}-undo`}
                    ><Undo2 size={11} /></button>
                  )}
                  {isDirty && <Save size={11} className="text-orange-600 animate-pulse" />}
                  {showSaved && <Check size={11} className="text-emerald-600" />}
                </div>
                <div className="px-3 py-2">
                  <textarea
                    value={value}
                    onChange={e => setField(cell.key, e.target.value)}
                    onKeyDown={e => { if ((e.ctrlKey || e.metaKey) && e.key === 'z' && canUndo) { e.preventDefault(); undoField(cell.key); } }}
                    onFocus={onFieldFocus}
                    data-field-key={cell.key}
                    data-testid={`status-${statusKey}-${cell.key}`}
                    rows={cell.rows || 3}
                    placeholder={cell.kind === 'lines' ? 'One item per line…' : ''}
                    className={`w-full px-2 py-1 border rounded text-[12px] font-mono resize-y focus:outline-none ${missing ? 'border-amber-300 bg-white' : 'border-emerald-300 focus:border-emerald-600'}`}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Token palette (module-scoped, mirrors QuotationLetterEditor) ─────
function TokenPalette({ onInsert }) {
  return (
    <div
      data-testid="status-token-palette"
      className="border border-slate-200 rounded-md bg-white px-3 py-2 space-y-1"
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Insert Field</span>
        <span className="text-[10px] italic text-slate-400">— click a text field first, then a chip to splice at the caret</span>
      </div>
      {TOKEN_GROUPS.map(grp => (
        <div key={grp.label} className="flex flex-wrap items-center gap-1">
          <span className="text-[10px] font-semibold text-slate-500 mr-1 uppercase tracking-wider min-w-[92px]">{grp.label}:</span>
          {grp.tokens.map(t => (
            <button
              key={t}
              type="button"
              data-testid={`status-insert-token-${t}`}
              onClick={() => onInsert(t)}
              title={`Insert {${t}} at cursor`}
              className="px-1.5 py-0.5 text-[10px] font-mono border border-slate-200 rounded bg-slate-50 hover:bg-[#316536] hover:text-white hover:border-[#316536] text-slate-700 transition-colors"
            >
              {`{${t}}`}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}
